import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ApiSidebar from './components/ApiSidebar';
import SearchBar from './components/SearchBar';
import QuickStart from './components/QuickStart';
import ApiEndpoint from './components/ApiEndpoint';
import CodeBlock from './components/CodeBlock';

// Import SDK components
import JavaScriptSDK from './components/sdks/JavaScriptSDK';
import PythonSDK from './components/sdks/PythonSDK';
import PhpSDK from './components/sdks/PhpSDK';
import JavaSDK from './components/sdks/JavaSDK';
import CSharpSDK from './components/sdks/CSharpSDK';

const ApiDocumentation = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.title = 'API Documentation - MessagingHub Pro';
  }, []);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const mockApiEndpoints = {
    'send-message': {
      endpoint: '/v1/messages',
      method: 'POST',
      title: 'Send Message',
      description: 'Send a text message to a single recipient or multiple recipients.',
      parameters: [
        {
          name: 'to',
          type: 'string',
          required: true,
          in: 'body',
          description: 'Recipient phone number in E.164 format',
          example: '+1234567890'
        },
        {
          name: 'from',
          type: 'string',
          required: true,
          in: 'body',
          description: 'Sender phone number (must be verified)',
          example: '+1987654321'
        },
        {
          name: 'body',
          type: 'string',
          required: true,
          in: 'body',
          description: 'Message content (max 1600 characters)',
          example: 'Hello from MessagingHub Pro!'
        },
        {
          name: 'mediaUrl',
          type: 'string',
          required: false,
          in: 'body',
          description: 'URL of media file to send (images, videos, documents)',
          example: 'https://example.com/image.jpg'
        }
      ],
      response: {
        messageId: 'msg_1234567890abcdef',
        status: 'queued',
        to: '+1234567890',
        from: '+1987654321',
        body: 'Hello from MessagingHub Pro!',
        createdAt: '2024-09-14T12:40:40.884Z',
        price: '0.0075',
        currency: 'USD'
      },
      examples: {
        curl: `curl -X POST https://api.messaginghub.pro/v1/messages \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+1234567890",
    "from": "+1987654321",
    "body": "Hello from MessagingHub Pro!"
  }'`,
        javascript: `const response = await fetch('https://api.messaginghub.pro/v1/messages', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '+1234567890',
    from: '+1987654321',
    body: 'Hello from MessagingHub Pro!'
  })
});

const result = await response.json();
console.log(result);`,
        python: `import requests

url = 'https://api.messaginghub.pro/v1/messages'
headers = {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
}
data = {
    'to': '+1234567890',
    'from': '+1987654321',
    'body': 'Hello from MessagingHub Pro!'
}

response = requests.post(url, headers=headers, json=data)
result = response.json()
print(result)`,
        php: `<?php
$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => 'https://api.messaginghub.pro/v1/messages',
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => array(
    'Authorization: Bearer YOUR_API_KEY',
    'Content-Type: application/json'
  ),
  CURLOPT_POSTFIELDS => json_encode(array(
    'to' => '+1234567890',
    'from' => '+1987654321',
    'body' => 'Hello from MessagingHub Pro!'
  ))
));

$response = curl_exec($curl);
curl_close($curl);

$result = json_decode($response, true);
print_r($result);
?>`
      }
    },
    'get-messages': {
      endpoint: '/v1/messages',
      method: 'GET',
      title: 'Get Messages',
      description: 'Retrieve a list of messages sent from your account.',
      parameters: [
        {
          name: 'limit',
          type: 'integer',
          required: false,
          in: 'query',
          description: 'Number of messages to return (max 100)',
          example: '20'
        },
        {
          name: 'offset',
          type: 'integer',
          required: false,
          in: 'query',
          description: 'Number of messages to skip',
          example: '0'
        },
        {
          name: 'status',
          type: 'string',
          required: false,
          in: 'query',
          description: 'Filter by message status',
          example: 'delivered'
        }
      ],
      response: {
        messages: [
          {
            messageId: 'msg_1234567890abcdef',
            status: 'delivered',
            to: '+1234567890',
            from: '+1987654321',
            body: 'Hello from MessagingHub Pro!',
            createdAt: '2024-09-14T12:40:40.884Z',
            deliveredAt: '2024-09-14T12:40:42.123Z',
            price: '0.0075',
            currency: 'USD'
          }
        ],
        pagination: {
          total: 150,
          limit: 20,
          offset: 0,
          hasMore: true
        }
      },
      examples: {
        curl: `curl -X GET "https://api.messaginghub.pro/v1/messages?limit=20&offset=0" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
        javascript: `const response = await fetch('https://api.messaginghub.pro/v1/messages?limit=20&offset=0', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

const result = await response.json();
console.log(result);`,
        python: `import requests

url = 'https://api.messaginghub.pro/v1/messages'
headers = {'Authorization': 'Bearer YOUR_API_KEY'}
params = {'limit': 20, 'offset': 0}

response = requests.get(url, headers=headers, params=params)
result = response.json()
print(result)`,
        php: `<?php
$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => 'https://api.messaginghub.pro/v1/messages?limit=20&offset=0',
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => array(
    'Authorization: Bearer YOUR_API_KEY'
  )
));

$response = curl_exec($curl);
curl_close($curl);

$result = json_decode($response, true);
print_r($result);
?>`
      }
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">MessagingHub Pro API</h1>
              <p className="text-lg text-muted-foreground">
                Build powerful messaging features into your applications with our comprehensive REST API. 
                Send SMS, MMS, and rich media messages with enterprise-grade reliability and global reach.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Zap" size={20} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Fast & Reliable</h3>
                </div>
                <p className="text-muted-foreground">
                  99.9% uptime SLA with global infrastructure and redundant systems for maximum reliability.
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Globe" size={20} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Global Reach</h3>
                </div>
                <p className="text-muted-foreground">
                  Send messages to 200+ countries with local phone numbers and carrier-grade delivery.
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Enterprise Security</h3>
                </div>
                <p className="text-muted-foreground">
                  SOC 2 Type II certified with end-to-end encryption and comprehensive audit logs.
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Base URL</h2>
              <CodeBlock
                code="https://api.messaginghub.pro"
                language="text"
                title="API Base URL"
                description="All API requests should be made to this base URL"
              />
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Authentication</h2>
              <p className="text-muted-foreground mb-4">
                All API requests require authentication using your API key in the Authorization header:
              </p>
              <CodeBlock
                code="Authorization: Bearer YOUR_API_KEY"
                language="text"
                title="Authentication Header"
                description="Include this header in all API requests"
              />
            </div>
          </div>
        );

      case 'quickstart':
        return <QuickStart />;

      // SDK Documentation
      case 'javascript-sdk':
        return <JavaScriptSDK />;
      case 'python-sdk':
        return <PythonSDK />;
      case 'php-sdk':
        return <PhpSDK />;
      case 'java-sdk':
        return <JavaSDK />;
      case 'csharp-sdk':
        return <CSharpSDK />;

      case 'rate-limits':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">Rate Limits</h1>
              <p className="text-lg text-muted-foreground">
                Understanding API rate limits and best practices for handling them in your applications.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Default Limits</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Messages per minute:</span>
                    <span className="font-medium text-foreground">100</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">API calls per minute:</span>
                    <span className="font-medium text-foreground">1,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Concurrent connections:</span>
                    <span className="font-medium text-foreground">10</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Enterprise Limits</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Messages per minute:</span>
                    <span className="font-medium text-foreground">1,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">API calls per minute:</span>
                    <span className="font-medium text-foreground">10,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Concurrent connections:</span>
                    <span className="font-medium text-foreground">100</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Rate Limit Headers</h2>
              <p className="text-muted-foreground mb-4">
                Every API response includes rate limit information in the headers:
              </p>
              <CodeBlock
                code={`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 999
X-RateLimit-Reset: 1631234567
X-RateLimit-Window: 60`}
                language="text"
                description="Rate limit headers included in every response"
              />
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Handling Rate Limits</h2>
              <p className="text-muted-foreground mb-4">
                When you exceed rate limits, the API returns a 429 status code. Here's how to handle it:
              </p>
              <CodeBlock
                code={`// JavaScript example with exponential backoff
async function sendMessageWithRetry(messageData, maxRetries = 3) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch('/v1/messages', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer YOUR_API_KEY',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
        continue;
      }

      return await response.json();
    } catch (error) {
      if (attempt === maxRetries - 1) throw error;
    }
  }
}`}
                language="javascript"
                title="Handling Rate Limits with Retry Logic"
              />
            </div>
          </div>
        );

      case 'send-message':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">Send Message</h1>
              <p className="text-lg text-muted-foreground">
                Send text messages, media messages, and rich content to your users worldwide.
              </p>
            </div>
            <ApiEndpoint {...mockApiEndpoints?.['send-message']} />
          </div>
        );

      case 'get-messages':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">Get Messages</h1>
              <p className="text-lg text-muted-foreground">
                Retrieve and filter messages sent from your account with pagination support.
              </p>
            </div>
            <ApiEndpoint {...mockApiEndpoints?.['get-messages']} />
          </div>
        );

      case 'api-keys':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">API Keys</h1>
              <p className="text-lg text-muted-foreground">
                Learn how to create, manage, and secure your API keys for authentication.
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Creating API Keys</h2>
              <p className="text-muted-foreground mb-4">
                API keys are created in your developer dashboard and come in two types:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-2">Test Keys</h3>
                  <p className="text-sm text-muted-foreground">
                    Start with <code className="bg-background px-1 rounded">mhp_test_</code> and work in sandbox mode
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-2">Live Keys</h3>
                  <p className="text-sm text-muted-foreground">
                    Start with <code className="bg-background px-1 rounded">mhp_live_</code> and send real messages
                  </p>
                </div>
              </div>
              <Button variant="default" iconName="Plus" iconPosition="left">
                Create New API Key
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Security Best Practices</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Shield" size={20} className="text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium text-foreground">Store Securely</h3>
                    <p className="text-sm text-muted-foreground">
                      Never commit API keys to version control. Use environment variables or secure key management.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="RotateCcw" size={20} className="text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium text-foreground">Rotate Regularly</h3>
                    <p className="text-sm text-muted-foreground">
                      Rotate your API keys every 90 days or immediately if compromised.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Eye" size={20} className="text-primary mt-0.5" />
                  <div>
                    <h3 className="font-medium text-foreground">Monitor Usage</h3>
                    <p className="text-sm text-muted-foreground">
                      Regularly review API key usage and disable unused keys.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-8">
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Section Not Found</h2>
              <p className="text-muted-foreground">
                The requested documentation section could not be found.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-16 flex">
        <ApiSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuToggle={handleMobileMenuToggle}
        />

        <main className="flex-1 lg:ml-80">
          <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMobileMenuToggle}
                  className="lg:hidden"
                >
                  <Icon name="Menu" size={20} />
                </Button>
                <SearchBar onSearch={handleSearch} />
              </div>
              
              <div className="flex items-center space-x-3">
                <Button variant="outline" size="sm" iconName="Download">
                  Download SDK
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  iconName="ExternalLink"
                  onClick={() => window.location.href = '/developer-portal'}
                >
                  Developer Portal
                </Button>
              </div>
            </div>
          </div>

          <div className="px-6 py-8">
            <div className="max-w-4xl">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ApiDocumentation;